import { call, put, select } from 'redux-saga/effects';
import { getReceiptRulesConnectionId } from '../selectors';
import {
  deleteRuleFromState,
  parseRuleDone,
  setRulesForApplicationConnection,
  setRuleState,
  updateEnableDiabledState,
} from '../actions';
import {
  deleteRuleRequest,
  enableDisableRuleRequest,
  getRuleForApplicationConnectionRequest,
  getRulesForApplicationConnectionRequest,
  parseRuleRequest,
  saveRuleRequest,
  updateRuleRequest,
} from '../api';
import {
  DeleteRuleRequestAction,
  EnableDisableRuleRequestAction,
  GetRuleForApplicationConnectionRequestAction,
  GetRulesForApplicationConnectionRequestAction,
  ParseRuleRequestAction,
  SaveRuleRequestAction,
  UpdateRuleRequestAction,
} from '../types';
import { handleApiCall, showMessage } from 'store/sagaHelper';
import i18n from 'i18next';

function* shouldUpdateLoadedRulesState(applicationConnectionId: string) {
  const loadedConnectionId: string | null = yield select(
    getReceiptRulesConnectionId
  );
  return loadedConnectionId === applicationConnectionId;
}

export function* saveRuleRequestSaga(action: SaveRuleRequestAction) {
  const { applicationConnectionId, rule } = action.payload;

  const onSuccess = function* (response) {
    const ruleId = response?.data?.data?.id;
    if (yield shouldUpdateLoadedRulesState(applicationConnectionId)) {
      yield put(setRuleState(response?.data?.data, ruleId));
    }
    yield call(
      showMessage,
      i18n.t('common.success'),
      i18n.t('organisation.extraction-rules.save-success')
    );
  };

  yield call(
    handleApiCall,
    saveRuleRequest,
    [applicationConnectionId, rule],
    onSuccess
  );
}

export function* updateRuleRequestSaga(action: UpdateRuleRequestAction) {
  const { applicationConnectionId, rule, ruleId } = action.payload;

  const onSuccess = function* (response) {
    if (yield shouldUpdateLoadedRulesState(applicationConnectionId)) {
      yield put(setRuleState(response?.data?.data, ruleId));
    }
    yield call(
      showMessage,
      i18n.t('common.success'),
      i18n.t('organisation.extraction-rules.save-success')
    );
  };

  yield call(
    handleApiCall,
    updateRuleRequest,
    [applicationConnectionId, rule, ruleId],
    onSuccess
  );
}

export function* getRulesForApplicationConnectionRequestSaga(
  action: GetRulesForApplicationConnectionRequestAction
) {
  const { applicationConnectionId } = action.payload;

  const onSuccess = function* (response) {
    yield put(
      setRulesForApplicationConnection(
        response?.data?.data,
        applicationConnectionId
      )
    );
  };

  yield call(
    handleApiCall,
    getRulesForApplicationConnectionRequest,
    [applicationConnectionId],
    onSuccess
  );
}

export function* getRuleForApplicationConnectionRequestSaga(
  action: GetRuleForApplicationConnectionRequestAction
) {
  const { applicationConnectionId, ruleId } = action.payload;

  const onSuccess = function* (response) {
    if (yield shouldUpdateLoadedRulesState(applicationConnectionId)) {
      yield put(setRuleState(response.data?.data, ruleId));
    }
  };

  yield call(
    handleApiCall,
    getRuleForApplicationConnectionRequest,
    [applicationConnectionId, ruleId],
    onSuccess
  );
}

export function* enableDisableRuleRequestSaga(
  action: EnableDisableRuleRequestAction
) {
  const { applicationConnectionId, ruleId, enabled } = action.payload;

  const onSuccess = function* () {
    if (yield shouldUpdateLoadedRulesState(applicationConnectionId)) {
      yield put(updateEnableDiabledState(ruleId, enabled));
    }
  };

  yield call(
    handleApiCall,
    enableDisableRuleRequest,
    [applicationConnectionId, ruleId, enabled],
    onSuccess
  );
}

export function* deleteRuleRequestSaga(action: DeleteRuleRequestAction) {
  const { applicationConnectionId, ruleId } = action.payload;

  const onSuccess = function* () {
    yield call(
      showMessage,
      i18n.t('organisation.extraction-rules.delete-success'),
      i18n.t('organisation.extraction-rules.delete-success-detail')
    );
    if (yield shouldUpdateLoadedRulesState(applicationConnectionId)) {
      yield put(deleteRuleFromState(ruleId));
    }
  };

  yield call(
    handleApiCall,
    deleteRuleRequest,
    [applicationConnectionId, ruleId],
    onSuccess
  );
}

export function* parseRuleRequestSaga(action: ParseRuleRequestAction) {
  const { sentence, tcNames } = action.payload;

  const onSuccess = function* (response) {
    yield put(parseRuleDone(response.data?.data));
  };

  yield call(handleApiCall, parseRuleRequest, [sentence, tcNames], onSuccess);
}
